/**
 * MagicBI Design System - TestimonialCard Component
 * 
 * Specialized card component for displaying customer testimonials
 * with ratings, quotes, and author information.
 */

import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Card, CardBody } from '../Card/Card';
import { Text } from '../../primitives/Typography/Typography';
import { Flex, Box } from '../../primitives/Layout/Layout';

// TestimonialCard props interface
export interface TestimonialCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Testimonial content/quote */
  content: string;
  /** Author name */
  authorName: string;
  /** Author role/title */
  authorRole: string;
  /** Author company */
  authorCompany?: string;
  /** Author avatar (initials or image URL) */
  authorAvatar?: string;
  /** Rating (1-5 stars) */
  rating?: number;
  /** Card variant */
  variant?: 'default' | 'elevated' | 'outlined';
  /** Card size */
  size?: 'sm' | 'md' | 'lg';
}

// Quote content
const QuoteContent = styled(Text)`
  font-style: italic;
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.semantic.inset.md};
  color: ${({ theme }) => theme.colors.text.primary};
  
  &::before {
    content: '"';
    font-size: 1.5em;
    color: ${({ theme }) => theme.colors.interactive.primary};
    line-height: 0;
  }
  
  &::after {
    content: '"';
    font-size: 1.5em;
    color: ${({ theme }) => theme.colors.interactive.primary};
    line-height: 0;
  }
`;

// Star rating
const StarRating = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: ${({ theme }) => theme.spacing.semantic.inset.sm};
`;

const Star = styled.div<{ $filled: boolean }>`
  width: 16px;
  height: 16px;
  color: ${({ $filled, theme }) => 
    $filled ? theme.colors.warning[400] : theme.colors.border.subtle
  };
  
  svg {
    width: 100%;
    height: 100%;
    fill: currentColor;
  }
`;

// Author avatar
const AuthorAvatar = styled.div<{ $imageUrl?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.inverse};
  flex-shrink: 0;
  
  ${({ $imageUrl, theme }) => $imageUrl ? css`
    background-image: url(${$imageUrl});
    background-size: cover;
    background-position: center;
  ` : css`
    background: ${theme.colors.background.gradientPrimary};
  `}
`;

// Author info
const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled(Text)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2px;
`;

const AuthorDetails = styled(Text)`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// Styled testimonial card
const StyledTestimonialCard = styled(Card)`
  transition: ${({ theme }) => theme.transitions.semantic.interactive.button.all};
  
  &:hover {
    transform: translateY(-2px);
  }
`;

// Star icon component
const StarIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
  <Star $filled={filled}>
    <svg viewBox="0 0 24 24">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>
  </Star>
);

// TestimonialCard component
export const TestimonialCard = forwardRef<HTMLDivElement, TestimonialCardProps>(
  (
    {
      content,
      authorName,
      authorRole,
      authorCompany,
      authorAvatar,
      rating = 5,
      variant = 'default',
      size = 'md',
      ...props
    },
    ref
  ) => {
    // Generate author initials if no avatar provided
    const getAuthorInitials = (name: string) => {
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    const isImageUrl = authorAvatar && (
      authorAvatar.startsWith('http') || 
      authorAvatar.startsWith('/') || 
      authorAvatar.includes('.')
    );

    return (
      <StyledTestimonialCard
        ref={ref}
        variant={variant}
        size={size}
        {...props}
      >
        <CardBody>
          {rating > 0 && (
            <StarRating>
              {[1, 2, 3, 4, 5].map((star) => (
                <StarIcon key={star} filled={star <= rating} />
              ))}
            </StarRating>
          )}
          
          <QuoteContent>
            {content}
          </QuoteContent>
          
          <Flex alignItems="center" gap="md">
            <AuthorAvatar $imageUrl={isImageUrl ? authorAvatar : undefined}>
              {!isImageUrl && (authorAvatar || getAuthorInitials(authorName))}
            </AuthorAvatar>
            
            <AuthorInfo>
              <AuthorName>{authorName}</AuthorName>
              <AuthorDetails>
                {authorRole}
                {authorCompany && ` at ${authorCompany}`}
              </AuthorDetails>
            </AuthorInfo>
          </Flex>
        </CardBody>
      </StyledTestimonialCard>
    );
  }
);

TestimonialCard.displayName = 'TestimonialCard';

export default TestimonialCard;
